import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, inject, Input, OnChanges, OnInit, Output, signal, Signal, SimpleChanges, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DropdownService } from 'src/utils/dropdown.service';

@Component({
  selector: 'app-dropdown-date',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './dropdown-date.component.html',
  styleUrl: './dropdown-date.component.scss',
})
export class DropdownDateComponent implements OnInit, OnChanges {
  @Input() dropdownId!: string;
  @Input() hideTrigger: boolean = false;
  @Input() initialStartDate?: string;
  @Input() initialEndDate?: string;
  @Output() dateRangeSelected = new EventEmitter<{ start: Date; end: Date }>();
  @Output() dateRangeCleared = new EventEmitter<void>();
  private readonly dropService = inject(DropdownService);

  placeholder: string = 'Fecha';

  // Propiedades para los inputs de fecha y hora
  startDateString: string = '';
  startTimeString: string = '';
  endDateString: string = '';
  endTimeString: string = '';

  // Señales para las fechas completas
  startDateTime: WritableSignal<Date | null> = signal(null);
  endDateTime: WritableSignal<Date | null> = signal(null);

  isOpen: Signal<boolean> = computed(
    () => this.dropService.getOpenDropdown()() === this.dropdownId
  );

  ngOnInit(): void {
    this.applyInputDates();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialStartDate'] || changes['initialEndDate']) {
      this.applyInputDates();
    }
  }

  private applyInputDates(): void {
    if (this.initialStartDate) {
      this.startDateString = this.initialStartDate.split('T')[0] ?? '';
      this.startTimeString = this.initialStartDate.split('T')[1]?.substring(0, 5) ?? '';
      this.updateDateTime('start');
    } else {
      this.startDateString = '';
      this.startTimeString = '';
      this.startDateTime.set(null);
    }
    if (this.initialEndDate) {
      this.endDateString = this.initialEndDate.split('T')[0] ?? '';
      this.endTimeString = this.initialEndDate.split('T')[1]?.substring(0, 5) ?? '';
      this.updateDateTime('end');
    } else {
      this.endDateString = '';
      this.endTimeString = '';
      this.endDateTime.set(null);
    }
  }

  toggleDropdown() {
    this.dropService.toggleDropdown(this.dropdownId);
  }

  // Obtener fecha de un string DateTime (yyyy-MM-ddTHH:mm:ss)
  getDateFromDateTime(dateTime?: string): string {
    if (!dateTime) return '';
    return dateTime.split('T')[0] || '';
  }

  // Obtener hora de un string DateTime (yyyy-MM-ddTHH:mm:ss)
  getTimeFromDateTime(dateTime?: string): string {
    if (!dateTime) return '';
    const timePart = dateTime.split('T')[1];
    if (!timePart) return '';
    return timePart.substring(0, 5); // HH:mm
  }

  // Manejar cambio en fecha
  onDateChange(type: 'start' | 'end', value: string): void {
    if (type === 'start') {
      this.startDateString = value;
      this.updateDateTime('start');
    } else {
      this.endDateString = value;
      this.updateDateTime('end');
    }
  }

  // Manejar cambio en hora
  onTimeChange(type: 'start' | 'end', value: string): void {
    if (type === 'start') {
      this.startTimeString = value;
      this.updateDateTime('start');
    } else {
      this.endTimeString = value;
      this.updateDateTime('end');
    }
  }

  // Actualizar el DateTime completo
  private updateDateTime(type: 'start' | 'end'): void {
    const dateStr = type === 'start' ? this.startDateString : this.endDateString;
    const timeStr = type === 'start' ? this.startTimeString : this.endTimeString;

    if (!dateStr) {
      if (type === 'start') {
        this.startDateTime.set(null);
      } else {
        this.endDateTime.set(null);
      }
      return;
    }

    // Si no hay hora, usar 00:00
    const time = timeStr || '00:00';
    const dateTimeString = `${dateStr}T${time}:00`;
    const date = new Date(dateTimeString);

    if (!isNaN(date.getTime())) {
      if (type === 'start') {
        this.startDateTime.set(date);
      } else {
        this.endDateTime.set(date);
      }
    }
  }

  // Aplicar el filtro
  applyFilter(): void {
    const start = this.startDateTime();
    const end = this.endDateTime();

    if (start && end) {
      this.dateRangeSelected.emit({ start, end });
      this.dropService.closeDropdown();
    }
  }

  // Limpiar el filtro
  clearFilter(): void {
    this.startDateString = '';
    this.startTimeString = '';
    this.endDateString = '';
    this.endTimeString = '';
    this.startDateTime.set(null);
    this.endDateTime.set(null);
  }

  formatDate(date: Date | null): string {
    if (!date) return '';
    return date.toLocaleDateString('es-ES');
  }

  clearSelection(): void {
    this.clearFilter();
    this.dateRangeCleared.emit();
    this.dropService.closeDropdown();
  }
}
